import React, { useState, useEffect, useRef } from 'react';
import Shape from './Shape';
import { SHAPES } from './Shapes';

const ShapePicker = ({ shapePlaced }) => {
  const [selectedShape, setSelectedShape] = useState({ type: 'S', rotation: 1 });
  const isMounted = useRef(false);

  // Broadcast the current piece to Grid
  function announceCurrentPiece(type, rotation) {
    window._currentPiece = { type, rotation };
    window.dispatchEvent(new CustomEvent('piece:changed', { detail: { type, rotation } }));
  }

  const pickRandomShape = () => {
    const shapeTypes = Object.keys(SHAPES);
    const randomType = shapeTypes[Math.floor(Math.random() * shapeTypes.length)];
    const rotations = Object.keys(SHAPES[randomType]);
    const randomRotation = parseInt(rotations[Math.floor(Math.random() * rotations.length)], 10);

    setSelectedShape({ type: randomType, rotation: randomRotation });
    // announce immediately (state update is async)
    announceCurrentPiece(randomType, randomRotation);
  };

  // On first mount, announce the initial piece
  useEffect(() => {
    announceCurrentPiece(selectedShape.type, selectedShape.rotation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When a shape gets placed, pick a new one
  useEffect(() => {
    if (isMounted.current) {
      pickRandomShape();
    } else {
      isMounted.current = true;
    }
  }, [shapePlaced]); // eslint-disable-line

  // ===== Desktop dragstart (HTML5 DnD) per filled cell =====
  const handleCellDragStart = (e, row, col) => {
    const { type, rotation } = selectedShape;
    const config = SHAPES[type][rotation];

    // Payload for Grid
    e.dataTransfer.setData('shapeType', type);
    e.dataTransfer.setData('rotation', String(rotation));
    e.dataTransfer.setData('grabDx', String(row));
    e.dataTransfer.setData('grabDy', String(col));
    if (e.dataTransfer.setDragImage) {
      const gridEl = e.currentTarget.closest('.shape-grid');
      if (gridEl) {
        const rect = gridEl.getBoundingClientRect();
        const cols = 4, rows = 4;
        const cellW = rect.width / cols;
        const cellH = rect.height / rows;

        // draw only the filled cells (no grid) as drag image
        const dpr = window.devicePixelRatio || 1;
        const canvas = document.createElement('canvas');
        canvas.width  = Math.round(rect.width  * dpr);
        canvas.height = Math.round(rect.height * dpr);
        const ctx = canvas.getContext('2d');
        ctx.scale(dpr, dpr);
        ctx.fillStyle = '#4CAF50';
        for (const [dx, dy] of config) {
          const x = dy * cellW;
          const y = dx * cellH;
          ctx.fillRect(x, y, cellW, cellH);
        }

        const img = new Image();
        img.src = canvas.toDataURL();
        img.style.position = 'fixed';
        img.style.top = '-10000px';
        img.style.left = '-10000px';
        img.style.pointerEvents = 'none';
        img.style.opacity = '0.01';
        document.body.appendChild(img);

        const anchorX = (col + 0.5) * cellW;
        const anchorY = (row + 0.5) * cellH;

        const apply = () => { try { e.dataTransfer.setDragImage(img, anchorX, anchorY); } catch {} };
        if (img.complete) apply(); else img.onload = apply;

        const targetEl = e.currentTarget;
        const cleanup = () => {
          img.onload = null;
          if (img.parentNode) img.parentNode.removeChild(img);
        };
        targetEl.addEventListener('dragend', cleanup, { once: true });
      }
    }
  };

  // ===== Mobile pointer/touch fallback =====
  const handleCellPointerDown = (e, row, col) => {
    if (e.pointerType !== 'touch') return; // desktop uses native DnD
    e.preventDefault();

    const { type, rotation } = selectedShape;
    const payload = { shapeType: type, rotation, grabDx: row, grabDy: col };

    const onMove = (ev) => {
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      if (!el) return;
      const r = el.dataset ? parseInt(el.dataset.row, 10) : NaN;
      const c = el.dataset ? parseInt(el.dataset.col, 10) : NaN;
      if (Number.isFinite(r) && Number.isFinite(c) && window._gridMobileUpdatePreview) {
        window._gridMobileUpdatePreview(r, c, payload);
      }
    };
    const onUp = (ev) => {
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      if (el && el.dataset && window._gridMobileDrop) {
        const r = parseInt(el.dataset.row, 10);
        const c = parseInt(el.dataset.col, 10);
        window._gridMobileDrop(r, c, payload);
      } else if (window._gridMobileUpdatePreview) {
        window._gridMobileUpdatePreview(NaN, NaN, {}); // clear
      }
      cleanup();
    };
    const cleanup = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp, true);
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerup', onUp, true);
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
      <Shape
        shapeType={selectedShape.type}
        rotation={selectedShape.rotation}
        onDragStartCell={handleCellDragStart}
        onPointerDownCell={handleCellPointerDown}
      />
    </div>
  );
};

export default ShapePicker;
